import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { resolvePreset } from '@construction/shared';
import { auditApi, type AuditLogEntry } from '@/api/endpoints';
import {
  Alert,
  Button,
  DataTable,
  DetailList,
  DetailSection,
  Drawer,
  EmptyState,
  FilterBar,
  ListGroup,
  ListRow,
  PageHeader,
  Spinner,
  TableRow,
} from '@/components/ui';
import { FilterChip } from '@/features/filters/FilterChip';
import { RangeField } from '@/features/filters/RangeField';
import { OrgFilter } from '@/features/orgs';
import { usePermissions } from '@/lib/permissions';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { siteDateKey, siteDayRange } from '@/lib/site-time';

const PAGE_SIZE = 50;

/**
 * 操作类型 → 文案（显式映射，理由同 permission-labels.ts）。
 * 表里没有的操作原样显示 —— 新加了一种审计动作忘了补文案，至少还读得出是什么。
 */
const ACTION_LABEL_KEY: Record<string, string> = {
  'user.create': 'audit.actionUserCreate',
  'user.update': 'audit.actionUserUpdate',
  'user.resetPassword': 'audit.actionUserResetPassword',
  'user.setPassword': 'audit.actionUserSetPassword',
  'site.create': 'audit.actionSiteCreate',
  'site.update': 'audit.actionSiteUpdate',
  'site.member.add': 'audit.actionSiteMemberAdd',
  'site.member.rejoin': 'audit.actionSiteMemberRejoin',
  'site.member.remove': 'audit.actionSiteMemberRemove',
  'punch.review': 'audit.actionPunchReview',
  'punch.manual': 'audit.actionPunchManual',
  'punch.anomalyResolve': 'audit.actionPunchAnomalyResolve',
  'punch.anomalyUnresolve': 'audit.actionPunchAnomalyUnresolve',
  'purchase.review': 'audit.actionPurchaseReview',
  'role.create': 'audit.actionRoleCreate',
  'role.update': 'audit.actionRoleUpdate',
  'role.delete': 'audit.actionRoleDelete',
  // 单位（CR-073）
  'org.create': 'audit.actionOrgCreate',
  'org.update': 'audit.actionOrgUpdate',
  // 日报审核在 CR-023 取消了，库里还留着当时的记录
  'dailyLog.review': 'audit.actionDailyLogReview',
  // 下载（CR-043）：读操作里唯一记审计的
  'dailyLog.export': 'audit.actionDailyLogExport',
  'hiddenWork.export': 'audit.actionHiddenWorkExport',
  'purchase.export': 'audit.actionPurchaseExport',
  'attendance.export': 'audit.actionAttendanceExport',
  'drawing.export': 'audit.actionDrawingExport',
  'schedule.export': 'audit.actionScheduleExport',
  // 进度表核对、发布（CR-080）
  'schedule.save': 'audit.actionScheduleSave',
  'schedule.publish': 'audit.actionSchedulePublish',
};

/**
 * 对象类型 → 文案。`entityType` 是代码里的表名（SiteMember、MaterialPurchase），
 * 直接摆出来客户看不懂（CR-040 走查）。表里没有的原样显示，理由同上。
 */
const ENTITY_LABEL_KEY: Record<string, string> = {
  User: 'audit.entityUser',
  Role: 'audit.entityRole',
  Site: 'audit.entitySite',
  SiteMember: 'audit.entitySiteMember',
  Punch: 'audit.entityPunch',
  MaterialPurchase: 'audit.entityPurchase',
  DailyLog: 'audit.entityDailyLog',
  Drawing: 'audit.entityDrawing',
  Schedule: 'audit.entitySchedule',
  Org: 'audit.entityOrg',
};

/**
 * 审计日志（CR-040 补上的页面）。
 *
 * 「查看审计日志」这个权限 CR-038 就有了，接口也早就有，但网页上一直没有这一页 ——
 * 勾了等于没勾。客户决定补上。
 *
 * CR-082 按列表页模板重做：一排筛选胶囊（区间、操作、操作人、公司），右边条数（**取服务端 total**，不数当前页）；
 * 表格点一行，右边滑出这条的详情和「改了哪些字段、从什么改成什么」。
 * 改之前是整行展开一段 JSON，职位改了几项权限要自己对着两段数组找。
 * 审计日志不属于某个工地，区间按浏览器时区算。
 */
export function AuditLogsPage() {
  const { t, i18n } = useTranslation();
  const toMessage = useErrorMessage();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = siteDateKey(tz);

  const [range, setRange] = useState(() => resolvePreset('thisWeek', today));
  const [action, setAction] = useState<string | null>(null);
  // 操作人（CR-082）：值是账号 id 的字符串，选项来自 `audit-logs/actors`
  const [actorId, setActorId] = useState<string | null>(null);
  // 平台账号按单位筛（CR-073）。单位账号看到的本来就只有自己家的
  const { isPlatform } = usePermissions();
  const [org, setOrg] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const actions = useQuery({ queryKey: ['audit-actions'], queryFn: () => auditApi.actions() });
  const actors = useQuery({ queryKey: ['audit-actors'], queryFn: () => auditApi.actors() });

  const logs = useQuery({
    queryKey: ['audit-logs', range.from, range.to, action, actorId, org, page],
    queryFn: () =>
      auditApi.list({
        ...siteDayRange(range.from, range.to, tz),
        action: action ?? undefined,
        actorId: actorId !== null && /^\d+$/.test(actorId) ? Number(actorId) : undefined,
        org: org || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  const data = logs.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const opened = data?.items.find((entry) => entry.id === openId) ?? null;
  const actionLabel = (value: string) =>
    ACTION_LABEL_KEY[value] ? t(ACTION_LABEL_KEY[value]!) : value;
  // 有名字就写名字（CR-041）；对象已经查不到时才退回内部编号
  const entityLabel = (entry: AuditLogEntry) => {
    const type = ENTITY_LABEL_KEY[entry.entityType]
      ? t(ENTITY_LABEL_KEY[entry.entityType]!)
      : entry.entityType;
    return entry.entityLabel ? `${type} · ${entry.entityLabel}` : `${type} #${entry.entityId}`;
  };
  const actorName = (entry: AuditLogEntry) => entry.actor?.name ?? t('audit.systemActor');
  const time = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  const resetPage = () => setPage(1);

  return (
    <>
      <PageHeader title={t('audit.title')} description={t('audit.description')} />

      <FilterBar summary={data && data.total > 0 && t('audit.totalCount', { count: data.total })}>
        <RangeField
          from={range.from}
          to={range.to}
          today={today}
          presets={['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth']}
          onChange={(next) => {
            setRange(next);
            resetPage();
          }}
        />
        <FilterChip<string>
          label={t('audit.colAction')}
          value={action}
          onChange={(next) => {
            setAction(next);
            resetPage();
          }}
          options={[
            { value: null, label: t('audit.allActions') },
            ...(actions.data ?? []).map((value) => ({ value, label: actionLabel(value) })),
          ]}
        />
        <FilterChip<string>
          label={t('audit.colActor')}
          value={actorId}
          onChange={(next) => {
            setActorId(next);
            resetPage();
          }}
          options={[
            { value: null, label: t('audit.allActors') },
            ...(actors.data ?? []).map((user) => ({ value: String(user.id), label: user.name })),
          ]}
        />
        {isPlatform && (
          <OrgFilter
            value={org}
            onChange={(next) => {
              setOrg(next);
              resetPage();
            }}
            platformOption={t('audit.orgPlatform')}
          />
        )}
      </FilterBar>

      {logs.isLoading && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}
      {logs.isError && <Alert>{toMessage(logs.error)}</Alert>}
      {data && data.items.length === 0 && <EmptyState title={t('audit.empty')} />}

      {data && data.items.length > 0 && (
        <>
          {/* 窄屏、App：列表（CR-036，不左右滑） */}
          <ListGroup className="xl:hidden">
            {data.items.map((entry) => (
              <ListRow
                key={entry.id}
                onClick={() => setOpenId(entry.id)}
                selected={openId === entry.id}
                title={actionLabel(entry.action)}
                subtitle={`${actorName(entry)} · ${entityLabel(entry)}`}
                trailing={<span className="tabular-nums">{time(entry.createdAt)}</span>}
              />
            ))}
          </ListGroup>

          <DataTable
            minWidth="48rem"
            columns={[
              { key: 'time', label: t('audit.colTime'), className: 'w-32' },
              { key: 'actor', label: t('audit.colActor'), className: 'w-36' },
              { key: 'action', label: t('audit.colAction'), className: 'w-44' },
              { key: 'entity', label: t('audit.colEntity') },
              { key: 'changes', label: t('audit.colChanges'), className: 'w-64' },
            ]}
          >
            {data.items.map((entry) => (
              <TableRow key={entry.id} selected={openId === entry.id} onOpen={() => setOpenId(entry.id)}>
                <td className="whitespace-nowrap text-caption tabular-nums text-ink-3">{time(entry.createdAt)}</td>
                <td className="truncate text-ink-2">{actorName(entry)}</td>
                <td className="truncate font-medium text-ink">{actionLabel(entry.action)}</td>
                <td className="truncate text-ink-2">{entityLabel(entry)}</td>
                <td className="truncate font-mono text-caption text-ink-3">{changedFields(entry).join(', ')}</td>
              </TableRow>
            ))}
          </DataTable>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                {t('app.prev')}
              </Button>
              <span className="min-w-14 text-center text-caption tabular-nums text-ink-3">
                {page} / {totalPages}
              </span>
              <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                {t('app.next')}
              </Button>
            </div>
          )}
        </>
      )}

      <Drawer
        open={opened !== null}
        size="wide"
        title={opened ? actionLabel(opened.action) : ''}
        subtitle={opened && entityLabel(opened)}
        onClose={() => setOpenId(null)}
      >
        {opened && (
          <div className="flex flex-col gap-6">
            <DetailList
              items={[
                [
                  t('audit.colTime'),
                  <span className="tabular-nums">{new Date(opened.createdAt).toLocaleString(i18n.language)}</span>,
                ],
                [
                  t('audit.colActor'),
                  opened.actor
                    ? `${opened.actor.name} · ${t('user.employeeNo')} ${opened.actor.employeeNo}`
                    : t('audit.systemActor'),
                ],
                [t('audit.colAction'), actionLabel(opened.action)],
                [t('audit.colEntity'), entityLabel(opened)],
                opened.ip && [t('audit.ip'), <span className="tabular-nums">{opened.ip}</span>],
              ]}
            />
            <DetailSection title={t('audit.changes')}>
              <Diff entry={opened} />
            </DetailSection>
          </div>
        )}
      </Drawer>
    </>
  );
}

type Fields = Record<string, unknown>;

const asFields = (value: unknown): Fields =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Fields) : {};

/** 这条改了哪些字段（表格里的「改动」列）。服务端只记变化的字段（audit.service 的 diff） */
function changedFields(entry: AuditLogEntry): string[] {
  return [...new Set([...Object.keys(asFields(entry.before)), ...Object.keys(asFields(entry.after))])];
}

const isPrimitiveList = (value: unknown): value is Array<string | number | boolean> =>
  Array.isArray(value) && value.every((item) => item === null || typeof item !== 'object');

function showValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (isPrimitiveList(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * 改之前 / 改之后，一个字段一行。
 * 两边都是清单的（职位的权限）只列多了哪些、少了哪些 —— 二十几项里找出变的那一项是这页最常做的事。
 */
function Diff({ entry }: { entry: AuditLogEntry }) {
  const { t } = useTranslation();
  const before = asFields(entry.before);
  const after = asFields(entry.after);
  const keys = changedFields(entry);
  if (keys.length === 0) return <p className="text-body text-ink-3">{t('audit.noDiff')}</p>;
  const hasBefore = Object.keys(before).length > 0;

  return (
    <div className="overflow-hidden rounded-lg border border-rule">
      <table className="w-full table-fixed text-body">
        <thead>
          <tr className="bg-surface-2/60 text-left text-caption text-ink-3">
            <th className="w-[30%] px-3 py-2 font-medium">{t('audit.field')}</th>
            {hasBefore && <th className="px-3 py-2 font-medium">{t('audit.before')}</th>}
            <th className="px-3 py-2 font-medium">{t('audit.after')}</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => {
            const from = before[key];
            const to = after[key];
            const lists = isPrimitiveList(from) && isPrimitiveList(to);
            const added = lists ? to.filter((item) => !from.includes(item)) : [];
            const removed = lists ? from.filter((item) => !to.includes(item)) : [];
            return (
              <tr key={key} className="border-t border-rule align-top">
                <td className="break-all px-3 py-2 font-mono text-caption text-ink-2">{key}</td>
                {hasBefore && (
                  <td className="break-words px-3 py-2 text-ink-2">
                    {lists ? t('audit.itemCount', { count: from.length }) : showValue(from)}
                  </td>
                )}
                <td className="break-words px-3 py-2 text-ink">
                  {lists ? (
                    <>
                      <span className="block">{t('audit.itemCount', { count: to.length })}</span>
                      {added.map((item) => (
                        <span key={`+${String(item)}`} className="block font-mono text-caption text-ok">
                          + {String(item)}
                        </span>
                      ))}
                      {removed.map((item) => (
                        <span key={`-${String(item)}`} className="block font-mono text-caption text-danger">
                          − {String(item)}
                        </span>
                      ))}
                    </>
                  ) : (
                    showValue(to)
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
